import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Calendar, MapPin, Clock, Building2, AlertCircle, CheckCircle2,
  Loader2, XCircle, Info, Mail
} from 'lucide-react';

interface MyRegistrationData {
  registration: {
    candidate_id: string;
    full_name: string;
    email: string | null;
    mobile_number: string;
    status: string;
    waitlist_position: number | null;
    self_cancelled_at: string | null;
    registered_at: string | null;
    payment_status: string | null;
    payment_amount_nok: number;
    can_self_cancel: boolean;
    self_cancel_blocked_reason: string | null;
  };
  instance: {
    id: string;
    name: string;
    start_date: string | null;
    end_date: string | null;
    exam_scheduled_at: string | null;
    location: string | null;
    self_cancel_deadline_days: number;
    host_organization: string;
    host_email: string | null;
  };
}

const STATUS_LABELS: Record<string, string> = {
  registered: 'Påmeldt',
  waitlist: 'Venteliste',
  self_cancelled: 'Avmeldt',
  invited: 'Invitert til eksamen',
  exam_started: 'Eksamen pågår',
  exam_completed: 'Eksamen fullført',
  no_show_pending_reschedule: 'Ikke fremmøtt — venter på ny dato',
  graded: 'Vurdert',
  claimed: 'Attest hentet',
  certificate_issued: 'Attest utstedt'
};

const STATUS_COLORS: Record<string, string> = {
  registered: 'bg-green-100 text-green-800',
  waitlist: 'bg-yellow-100 text-yellow-800',
  self_cancelled: 'bg-gray-100 text-gray-800',
  invited: 'bg-blue-100 text-blue-800',
  exam_started: 'bg-blue-100 text-blue-800',
  exam_completed: 'bg-purple-100 text-purple-800',
  no_show_pending_reschedule: 'bg-orange-100 text-orange-800',
  graded: 'bg-purple-100 text-purple-800',
  claimed: 'bg-green-100 text-green-800',
  certificate_issued: 'bg-green-100 text-green-800'
};

export function MyRegistrationPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<MyRegistrationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelResult, setCancelResult] = useState<string | null>(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/my-registration/${token}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || json.error || 'Fant ikke påmeldingen');
      }
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke laste påmeldingen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCancel = async () => {
    if (!token) return;
    setCancelling(true);
    try {
      const res = await fetch(`/api/public/my-registration/${token}/cancel`, {
        method: 'POST'
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || json.error || 'Avmelding mislyktes');
      }
      setCancelResult(json.message);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Avmelding mislyktes');
    } finally {
      setCancelling(false);
      setConfirmingCancel(false);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString('no-NO', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatDateTime = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString('no-NO', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-kf-blue-700" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-kf-error-200 p-6 text-center">
          <AlertCircle className="w-12 h-12 text-kf-error-600 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Fant ikke påmeldingen</h1>
          <p className="text-gray-600">{error || 'Lenken er ugyldig.'}</p>
        </div>
      </div>
    );
  }

  const { registration, instance } = data;
  const statusColor = STATUS_COLORS[registration.status] || 'bg-gray-100 text-gray-800';
  const statusLabel = STATUS_LABELS[registration.status] || registration.status;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header — Kompetanseforbundet brand */}
          <div className="bg-gradient-to-r from-kf-blue-800 to-kf-blue-950 px-6 py-5 text-white border-b-4 border-kf-orange-600">
            <div className="text-sm text-kf-blue-100 mb-1">Min påmelding</div>
            <h1 className="text-2xl font-bold">{instance.name}</h1>
            <p className="text-kf-blue-100 mt-1 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {instance.host_organization}
            </p>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* Status */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Status:</span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}>
                {statusLabel}
              </span>
              {registration.status === 'waitlist' && registration.waitlist_position && (
                <span className="text-sm text-gray-600">
                  (Posisjon: #{registration.waitlist_position})
                </span>
              )}
            </div>

            {cancelResult && (
              <div className="bg-kf-success-50 border border-kf-success-200 rounded p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-kf-success-600 mt-0.5" />
                <div className="text-kf-blue-950">{cancelResult}</div>
              </div>
            )}

            {/* Kandidat-info */}
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-gray-500">Navn</div>
                <div className="font-medium">{registration.full_name}</div>
              </div>
              <div>
                <div className="text-gray-500">Mobil</div>
                <div className="font-medium">{registration.mobile_number}</div>
              </div>
              {registration.email && (
                <div>
                  <div className="text-gray-500">E-post</div>
                  <div className="font-medium">{registration.email}</div>
                </div>
              )}
              {registration.registered_at && (
                <div>
                  <div className="text-gray-500">Påmeldt</div>
                  <div className="font-medium">{formatDate(registration.registered_at)}</div>
                </div>
              )}
            </div>

            {/* Kurs-detaljer */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {instance.start_date && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-kf-blue-700 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Kursstart</div>
                    <div className="font-medium">{formatDate(instance.start_date)}</div>
                  </div>
                </div>
              )}
              {instance.exam_scheduled_at && (
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-kf-orange-600 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Eksamen</div>
                    <div className="font-medium">{formatDateTime(instance.exam_scheduled_at)}</div>
                  </div>
                </div>
              )}
              {instance.location && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Sted</div>
                    <div className="font-medium">{instance.location}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Betalingsstatus */}
            {registration.payment_status && registration.payment_status !== 'not_required' && (
              <div className="bg-kf-orange-50 border border-kf-orange-200 rounded p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-kf-orange-700 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-kf-blue-950">
                      Påmeldingsgebyr: {registration.payment_amount_nok.toLocaleString('no-NO')} kr
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                      Status: <strong>{registration.payment_status === 'pending' ? 'Avventer betaling' :
                        registration.payment_status === 'paid' ? 'Betalt' :
                        registration.payment_status === 'failed' ? 'Mislyktes' :
                        registration.payment_status}</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Selvavmelding */}
            {registration.status !== 'self_cancelled' && (
              <div className="border-t border-gray-200 pt-6">
                <h2 className="font-semibold text-gray-900 mb-2">Avmelding</h2>
                {registration.can_self_cancel ? (
                  <>
                    <p className="text-sm text-gray-700 mb-3">
                      Du kan melde deg av selv frem til {instance.self_cancel_deadline_days} dager før kursstart.
                      {registration.payment_amount_nok > 0 && (
                        <strong> Påmeldingsgebyret refunderes ikke.</strong>
                      )}
                    </p>
                    {!confirmingCancel ? (
                      <button
                        onClick={() => setConfirmingCancel(true)}
                        className="px-4 py-2 bg-kf-error-600 hover:bg-kf-error-700 text-white rounded font-medium transition-colors"
                      >
                        Meld meg av
                      </button>
                    ) : (
                      <div className="bg-kf-error-50 border border-kf-error-300 rounded p-4 space-y-3">
                        <p className="font-medium text-kf-error-800">
                          Er du sikker på at du vil melde deg av?
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCancel}
                            disabled={cancelling}
                            className="px-4 py-2 bg-kf-error-600 hover:bg-kf-error-700 disabled:bg-gray-400 text-white rounded font-medium transition-colors"
                          >
                            {cancelling ? 'Avmelder...' : 'Ja, meld meg av'}
                          </button>
                          <button
                            onClick={() => setConfirmingCancel(false)}
                            className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded font-medium"
                          >
                            Avbryt
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-gray-500 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {registration.self_cancel_blocked_reason === 'deadline_passed'
                            ? 'Selvavmelding er ikke lenger mulig'
                            : 'Selvavmelding er ikke mulig'}
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          {registration.self_cancel_blocked_reason === 'deadline_passed'
                            ? `Det er færre enn ${instance.self_cancel_deadline_days} dager igjen til kursstart. Kontakt kursarrangøren direkte.`
                            : `Kontakt kursarrangøren direkte for å avmelde.`}
                        </p>
                        {instance.host_email && (
                          <a
                            href={`mailto:${instance.host_email}`}
                            className="inline-flex items-center gap-1 text-kf-blue-700 hover:underline text-sm mt-2 font-medium"
                          >
                            <Mail className="w-4 h-4" /> {instance.host_email}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-gray-500">
          En tjeneste fra <strong className="text-kf-blue-950">Kompetanseforbundet</strong>
        </div>
      </div>
    </div>
  );
}
